import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useTranslation } from '../hooks/useTranslation';
import { User, LogOut, Shield, UserCircle, Loader2 } from 'lucide-react';
import { supabase, handleSupabaseError } from '../lib/supabase';
import { ProfileModal } from './ProfileModal';

export function UserMenu() {
  const { user, signOut, isAdmin } = useAuthStore();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [profile, setProfile] = useState<{
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    phone: string | null;
    secondary_phone: string | null;
    date_of_birth: string | null;
    avatar_url: string | null;
  } | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click was outside both the menu and the button
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Add event listener for clicks
    document.addEventListener('mousedown', handleClickOutside);
    
    // Add event listener for touch events
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Fetch profile with retry logic
  const fetchProfile = async () => {
    if (!user) return;
    
    try {
      setError(null);
      const data = await handleSupabaseError(
        () => supabase
          .from('profiles')
          .select('id, username, first_name, last_name, phone, secondary_phone, date_of_birth, avatar_url')
          .eq('id', user.id)
          .maybeSingle(),
        'fetchProfile'
      );
      
      if (data) {
        setProfile(data);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Could not load profile. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch profile when component mounts
  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) return null;

  const displayName = profile ? 
    profile.username || user.email?.split('@')[0] : 
    user.email?.split('@')[0];

  const initials = profile ? 
    `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase() :
    user.email?.[0]?.toUpperCase() || 'U';

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
        <span className="text-gray-600 dark:text-gray-300">{t.common.loading}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
      >
        {profile?.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt={displayName}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white">
            {initials}
          </div>
        )}
        <span>{displayName}</span>
      </button>

      {isOpen && (
        <div 
          ref={menuRef}
          className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50"
        >
          <button
            onClick={() => {
              setIsProfileModalOpen(true);
              setIsOpen(false);
            }}
            className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <UserCircle className="w-4 h-4 mr-2" />
            {t.profile.myAccount}
          </button>

          <Link
            to="/dashboard"
            className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => setIsOpen(false)}
          >
            <User className="w-4 h-4 mr-2" />
            {t.common.dashboard}
          </Link>

          {isAdmin && (
            <Link
              to="/admin"
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setIsOpen(false)}
            >
              <Shield className="w-4 h-4 mr-2" />
              Admin Panel
            </Link>
          )}

          <hr className="my-1 border-gray-200 dark:border-gray-700" />

          <button
            onClick={handleSignOut}
            className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t.common.logout}
          </button>
        </div>
      )}

      {isProfileModalOpen && (
        <ProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          profile={profile}
          onProfileUpdated={() => {
            if (user) {
              fetchProfile();
            }
          }}
        />
      )}
    </div>
  );
}